import React from 'react';
import { useNavigate } from 'react-router-dom';

// Three editions, side by side on the home page. The order is the honest one — free first, because
// it is the one most clubs should start with, and it is not a crippled trial: it stays free.
//
// The middle edition is described the way it actually behaves: it hands the operator music and video
// controls, but nothing runs by itself. Selling it as "entertainment made easy" would set up the
// wrong expectation, and the operator finds out in front of a full hall.

const translations = {
  de: {
    tag: 'Editionen',
    title: 'Drei Editionen — von kostenlos bis vollautomatisch',
    subtitle:
      'Alle drei zeigen dasselbe Scoreboard. Der Unterschied liegt darin, wie viel Unterhaltung dazukommt — und wie viel davon die bedienende Person selbst auslösen muss.',
    editions: [
      {
        name: 'Web',
        price: 'Kostenlos',
        priceNote: 'und bleibt es',
        pitch: 'Das vollständige Scoreboard im Browser. Ohne Installation, ohne Konto.',
        points: [
          'Spielzeit, Strafen, Teamfouls, Timeouts',
          'Zweiter Bildschirm für die Halle',
          'Vereinsfarbe und Sponsorenlogos',
        ],
        cta: 'Scoreboard starten',
        to: '/control',
      },
      {
        name: 'Web mit Entertainment',
        price: 'CHF 150',
        priceNote: 'pro Jahr',
        pitch: 'Dasselbe Scoreboard, dazu Musik und Videos — von Hand gesteuert.',
        points: [
          'Musik und Videos aus dem Browser',
          'Pre-Game- und Sponsorenvideos',
          'Alles wird manuell gestartet und gestoppt',
        ],
        note: 'Setzt eine eingespielte Bedienung voraus: während des Spiels bleibt eine Hand an der Musik.',
        cta: 'Preise ansehen',
        to: '/preise',
      },
      {
        name: 'Desktop',
        price: 'CHF 250',
        priceNote: 'pro Jahr · 30 Tage testen',
        pitch: 'Alles aus der Web-Version — und die Unterhaltung läuft von selbst.',
        points: [
          'Teamlogos und Kader hinterlegt',
          'Videos nahtlos eingebunden',
          'Musik vollautomatisch',
          'Torjubel pro Spielerin, Shutout-Jubel für die Torhüterin',
        ],
        cta: 'Desktop ansehen',
        to: '/desktop',
        highlight: true,
        badge: 'Neu',
      },
    ],
  },
  en: {
    tag: 'Editions',
    title: 'Three editions — from free to fully automatic',
    subtitle:
      'All three show the same scoreboard. What differs is how much entertainment comes with it — and how much of it the operator has to trigger themselves.',
    editions: [
      {
        name: 'Web',
        price: 'Free',
        priceNote: 'and staying that way',
        pitch: 'The complete scoreboard in the browser. No installation, no account.',
        points: [
          'Game clock, penalties, team fouls, timeouts',
          'Second screen for the venue',
          'Club colour and sponsor logos',
        ],
        cta: 'Open the scoreboard',
        to: '/control',
      },
      {
        name: 'Web with Entertainment',
        price: 'CHF 150',
        priceNote: 'per year',
        pitch: 'The same scoreboard plus music and video — driven by hand.',
        points: [
          'Music and video from the browser',
          'Pre-game and sponsor videos',
          'Everything is started and stopped manually',
        ],
        note: 'Assumes a practised operator: during the match one hand stays on the music.',
        cta: 'See pricing',
        to: '/preise',
      },
      {
        name: 'Desktop',
        price: 'CHF 250',
        priceNote: 'per year · 30-day trial',
        pitch: 'Everything the web edition does — and the entertainment runs itself.',
        points: [
          'Team logos and rosters built in',
          'Video woven into the match',
          'Music fully automatic',
          'Goal celebrations per player, a shutout celebration for the goalkeeper',
        ],
        cta: 'See the desktop edition',
        to: '/desktop',
        highlight: true,
        badge: 'New',
      },
    ],
  },
};

export default function EditionsSection({ lang = 'de' }) {
  const t = translations[lang] || translations.de;
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center px-8 py-24 w-full bg-gray-50 max-md:px-5 max-md:py-16">
      <div className="flex flex-col w-full max-w-[1200px]">
        <div className="flex flex-col max-w-[800px] w-full mb-14 text-center mx-auto">
          <div className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">{t.tag}</div>
          <h2 id="editions-heading" className="text-5xl font-extrabold leading-tight text-stone-900 max-md:text-3xl mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-stone-600">{t.subtitle}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {t.editions.map((e) => (
            <div
              key={e.name}
              className={`relative flex flex-col rounded-2xl border bg-white p-8 ${
                e.highlight ? 'border-blue-600 shadow-xl md:-mt-4 md:mb-4' : 'border-stone-200 shadow-sm'
              }`}
            >
              {e.badge && (
                <span className="absolute -top-3 left-8 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">
                  {e.badge}
                </span>
              )}
              <div className="text-xl font-bold text-stone-900">{e.name}</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-stone-900">{e.price}</span>
                <span className="text-sm text-stone-500">{e.priceNote}</span>
              </div>
              <p className="mt-4 text-base leading-relaxed text-stone-600">{e.pitch}</p>

              <ul className="mt-6 space-y-2.5 flex-grow">
                {e.points.map((p) => (
                  <li key={p} className="flex gap-2.5 text-base text-stone-700">
                    <span className="mt-1 text-blue-600" aria-hidden="true">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>

              {/* Said out loud rather than in a footnote: this edition asks something of the operator. */}
              {e.note && <p className="mt-6 rounded-lg bg-amber-50 p-3 text-sm leading-relaxed text-amber-900">{e.note}</p>}

              <button
                type="button"
                onClick={() => navigate(e.to)}
                className={`mt-8 w-full rounded-lg px-5 py-3 text-sm font-semibold transition-colors ${
                  e.highlight
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'border border-stone-300 text-stone-800 hover:bg-stone-50'
                }`}
              >
                {e.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
